import { UIKit } from "@/shared/ui";
import { isSwitch, switchKeyCount, type CatalogItem } from "@/shared/catalog";
import { cableEndCatalogItem } from "@/widgets/electrical";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { useWiringStore } from "@/shared/store/layers";
import { remove } from "@/shared/store/actions";

export type PropertiesPanelCatalogItem = CatalogItem;

export const LightingConnectionProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const updateConnection = useWiringStore((s) => s.updateLightingConnection);

	// Хелпер для updateFixture в зависимости от каталога

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.LightingConnection) {
		const connection = layers.wiring.connections[selection.id];
		if (!connection) return null;
		const nameOf = (id: string) => cableEndCatalogItem(layers.electrical, id)?.name ?? "Прибор";
		const switchId = isSwitch(cableEndCatalogItem(layers.electrical, connection.from))
			? connection.from
			: connection.to;
		const lightId = switchId === connection.from ? connection.to : connection.from;
		const switchFixture = layers.electrical.fixtures[switchId];
		const keyCount = switchKeyCount(switchFixture?.ref.item ?? "");
		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					Связь освещения
				</UIKit.Text>
				<div className="flex flex-col gap-1 rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
					<UIKit.Text as="span">
						Выключатель: <b className="text-ink">{nameOf(switchId)}</b>
					</UIKit.Text>
					<UIKit.Text as="span">
						Светильник: <b className="text-ink">{nameOf(lightId)}</b>
					</UIKit.Text>
					<UIKit.Text as="span">Линия: {connection.style === "arc" ? "дуга" : "прямая"}</UIKit.Text>
				</div>
				{keyCount > 1 && (
					<UIKit.Select
						label="Клавиша"
						value={connection.key ?? 1}
						options={([1, 2, 3] as const)
							.filter((key) => key <= keyCount)
							.map((key) => ({ value: key, label: `Клавиша ${key}` }))}
						onChange={(key) =>
							useProjectStore.getState().recordEdit(() => updateConnection(connection.id, { key }))
						}
					/>
				)}
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
					onClick={onDelete}
				>
					Удалить связь
				</UIKit.Button>
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
